import { useId, useState, type FormEvent } from "react";
import { DEFAULT_SEVERITY, SUPPORT_AREA_LABEL, SUPPORT_AREAS, SUPPORT_SEVERITIES, SUPPORT_SEVERITY_LABEL, type CreateSupportRequest, type SupportArea, type SupportKind, type SupportRequest } from "@lms/shared-types";
import { ApiRequestError, fetchJson, getLastApiFailure } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { LMS_VERSION } from "../../config";
import { Button, Dialog, DialogAlert, inputClass, labelClass } from "../ui";
import { guessArea } from "./guessArea";

/**
 * One form, three flavours (PLT-09; the template's SupportRequestDialog):
 * open a ticket, request a feature, report a bug. The person is already
 * lost or annoyed, so it asks for the least it can — what it is about, how
 * bad, what happened — and carries the rest itself: who they are, the page
 * they were on, the build, the browser and the last failed request's id.
 *
 * Typing starts in the description, not the page name (the template's
 * pitfall 1); the middle severity is chosen, never the alarm; and the
 * confirmation replaces the form inside this dialog with focus on Done, not
 * in the panel behind it (pitfall 2). An error is said inside the dialog,
 * where a screen-reader user who is in it will hear it (gairm/lms#203 §4).
 */
export interface PageContext {
  title: string;
  path: string;
}

export const KIND_TITLE: Record<SupportKind, string> = { ticket: "Open a ticket", feature: "Request a feature", bug: "Report a bug" };
const KIND_LEAD: Record<SupportKind, string> = {
  ticket: "Something is not working for you and you need a hand.",
  feature: "Something this should do, or do better.",
  bug: "Something did the wrong thing, or nothing at all.",
};
const BODY_LABEL: Record<SupportKind, string> = { ticket: "What do you need?", feature: "What should it do?", bug: "What happened?" };
const SEVERITY_LABEL: Record<SupportKind, string> = { ticket: "", feature: "How much does it matter?", bug: "How bad is it?" };
export const KIND_NOUN: Record<SupportKind, string> = { ticket: "Ticket", feature: "Feature request", bug: "Bug report" };

export function SupportRequestDialog({ kind, page, onClose }: { kind: SupportKind; page: PageContext; onClose: () => void }) {
  const { me, session, config } = useAuth();
  const [area, setArea] = useState<SupportArea>(guessArea(page.path));
  const [severity, setSeverity] = useState<string>(DEFAULT_SEVERITY[kind] ?? "");
  const [pageTitle, setPageTitle] = useState(page.title);
  const [body, setBody] = useState("");
  const [expected, setExpected] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const [sent, setSent] = useState<SupportRequest | null>(null);
  const ids = { page: useId(), area: useId(), severity: useId(), body: useId(), bodyErr: useId(), expected: useId(), sent: useId(), bodyHint: useId() };
  const missing = body.trim().length === 0;
  const severities = SUPPORT_SEVERITIES[kind] as readonly string[];

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (missing || !session) return;
    setBusy(true);
    setError(undefined);
    const failure = getLastApiFailure();
    const request: CreateSupportRequest = {
      kind,
      area,
      severity: severities.length > 0 ? severity : null,
      body: body.trim(),
      expected: kind === "bug" && expected.trim() ? expected.trim() : null,
      page: { title: pageTitle.trim() || null, path: page.path },
      appVersion: LMS_VERSION,
      userAgent: typeof navigator === "undefined" ? null : navigator.userAgent.slice(0, 400),
      lastRequestId: failure?.requestId ?? null,
    };
    try {
      setSent(await fetchJson<SupportRequest>(config, session, "/v1/support/requests", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const field = inputClass;
  return (
    // One dialog for the form and its confirmation, so the status element below stays mounted across the change and
    // is FILLED rather than inserted (a region that arrives with its words is often not read); Done is described by it,
    // so the number is read again when focus lands there.
    <Dialog title={KIND_TITLE[kind]} onClose={onClose} wide {...(sent ? { closeLabel: "Done", focusKey: "sent", closeFocus: true, closeDescribedBy: ids.sent } : {})}>
      {/* Inside the modal, where the reader is: the page behind is inert while it is open, the shell's live region with it. */}
      <p role="status" id={ids.sent} data-testid="support-sent" className={sent ? "rounded-lg border border-ok bg-surface p-3 text-ink" : "sr-only"}>
        {sent && (
          <>
            <span aria-hidden="true" className="mr-1 text-ok">
              ✓
            </span>
            {KIND_NOUN[sent.kind]} <strong>{sent.number}</strong> is recorded, with the page and the details it needs.{" "}
            {sent.handoff.status === "handed_off" ? `It is with the support desk${sent.handoff.caseReference ? ` as case ${sent.handoff.caseReference}` : ""}. ` : ""}
            It is under <em>My requests</em>; quote the number if you mention it to anyone.
          </>
        )}
      </p>
      {!sent && (
        <form onSubmit={(e) => void submit(e)} noValidate className="grid gap-4" data-testid="support-form">
          <p className="text-muted">{KIND_LEAD[kind]}</p>
          <div className="grid gap-2 rounded-lg border border-line bg-canvas p-3 text-sm">
            <p className="min-w-0 break-words">
              <span className="font-medium">From </span>
              {me ? `${me.person.displayName} · ${me.person.email}` : "you"}
            </p>
            <div className="grid gap-1">
              <label htmlFor={ids.page} className={labelClass}>
                Page
              </label>
              <input id={ids.page} value={pageTitle} onChange={(e) => setPageTitle(e.target.value)} maxLength={200} className={field} />
              <p className="break-all font-mono text-xs text-muted">{page.path}</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid min-w-0 gap-1">
              <label htmlFor={ids.area} className={labelClass}>
                What is it about?
              </label>
              <select id={ids.area} value={area} onChange={(e) => setArea(e.target.value as SupportArea)} className={field}>
                {SUPPORT_AREAS.map((a) => (
                  <option key={a} value={a}>
                    {SUPPORT_AREA_LABEL[a]}
                  </option>
                ))}
              </select>
            </div>
            {severities.length > 0 && (
              <div className="grid min-w-0 gap-1">
                <label htmlFor={ids.severity} className={labelClass}>
                  {SEVERITY_LABEL[kind]}
                </label>
                <select id={ids.severity} value={severity} onChange={(e) => setSeverity(e.target.value)} className={field}>
                  {severities.map((s) => (
                    <option key={s} value={s}>
                      {SUPPORT_SEVERITY_LABEL[s as keyof typeof SUPPORT_SEVERITY_LABEL]}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="grid gap-1">
            <label htmlFor={ids.body} className={labelClass}>
              {BODY_LABEL[kind]} <span className="text-muted">(required)</span>
            </label>
            {/* ADR-0022: the words go to a support desk that works other organisations' cases too. */}
            <p id={ids.bodyHint} className="text-sm text-muted">
              It goes to the support desk, which works for other organisations too. Please leave out details of a disability, an accommodation or your health — your program director is the person for those.
            </p>
            <textarea
              id={ids.body}
              data-autofocus
              rows={4}
              maxLength={4000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              aria-required="true"
              aria-invalid={touched && missing ? true : undefined}
              aria-describedby={touched && missing ? `${ids.bodyHint} ${ids.bodyErr}` : ids.bodyHint}
              className={field}
            />
            {touched && missing && (
              <p id={ids.bodyErr} role="alert" className="text-sm font-medium text-danger">
                Say what you need — this is the one thing we have to have.
              </p>
            )}
          </div>
          {kind === "bug" && (
            <div className="grid gap-1">
              <label htmlFor={ids.expected} className={labelClass}>
                What did you expect? <span className="text-muted">(optional)</span>
              </label>
              <textarea id={ids.expected} rows={2} maxLength={2000} value={expected} onChange={(e) => setExpected(e.target.value)} className={field} />
            </div>
          )}
          <p className="text-sm text-muted">It is sent with the page, this build ({LMS_VERSION}), your browser and the id of the last request that failed here, so you do not have to find them.</p>
          {error !== undefined && (
            <DialogAlert focusKey={error} className="text-sm font-medium text-danger">
              It was not sent: {error instanceof ApiRequestError ? `${error.message}${error.requestId ? ` (request ${error.requestId})` : ""}` : "the service could not be reached."} Nothing you typed is lost — try again.
            </DialogAlert>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" disabled={busy} data-testid="support-send">
              {busy ? "Sending…" : "Send"}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
